import { BROWSER_EXCLUDE_FIELDS_PARAM, typesenseQueryBy } from '@shared/core/search';
import {
  TYPESENSE_MAX_CANDIDATES,
  TYPESENSE_MAX_PER_PAGE,
  toTypesenseBoostSort,
  toTypesenseFilter,
} from '@shared/core/search/dialects/typesense';
import { type LotTypesenseDoc, lotDocFromTypesense } from '@shared/core/search/entities/lotTypesense';
import { z } from 'zod';

import { postWithFailover } from './failover';
import {
  isAuthStatus,
  type MultiSearchLeg,
  type MultiSearchResult,
  type MultiSearchTransport,
  SearchAuthError,
  type SearchTransport,
} from './types';

const responseSchema = z.object({
  results: z.array(
    z.object({
      hits: z.array(z.object({ document: z.unknown() })).optional(),
      code: z.number().optional(),
      error: z.string().optional(),
    })
  ),
});

// Tolerant by design: an odd highlight or facet shape degrades to empty, never fails the whole response.
const multiSearchResultSchema = z.object({
  found: z.number().catch(0),
  hits: z
    .array(
      z.object({
        document: z.record(z.string(), z.unknown()),
        text_match: z.number().optional(),
        highlight: z.record(z.string(), z.object({ snippet: z.string().optional() }).catch({})).catch({}),
      })
    )
    .catch([]),
  facet_counts: z
    .array(
      z.object({
        field_name: z.string(),
        counts: z.array(z.object({ value: z.string(), count: z.number() })).catch([]),
      })
    )
    .catch([]),
  error: z.string().optional(),
  code: z.number().optional(),
});

const multiSearchResponseSchema = z.object({ results: z.array(multiSearchResultSchema).catch([]) });

const DOCUMENT_MAPPERS: Readonly<Record<string, (document: unknown) => unknown>> = {
  lot: (document) => lotDocFromTypesense(document as LotTypesenseDoc),
};

export const parseOrigins = (endpoint: string): string[] =>
  endpoint
    .split(',')
    .map((entry) => entry.trim())
    .flatMap((entry) => {
      try {
        return [new URL(entry).origin];
      } catch {
        return [];
      }
    });

/** Posts a multi_search body, failing over across the endpoint's nodes. */
async function postMultiSearch(endpoint: string, key: string, body: string, signal?: AbortSignal): Promise<unknown> {
  const origins = parseOrigins(endpoint);
  if (origins.length === 0) throw new Error('Typesense endpoint has no usable node');
  const headers = { 'Content-Type': 'application/json', 'X-TYPESENSE-API-KEY': key };
  return postWithFailover(
    origins,
    (origin) => ({ url: `${origin}/multi_search`, init: { method: 'POST', headers, body } }),
    signal,
    'Typesense'
  );
}

/** The Typesense `searches` entry for one leg of a multi-collection request. */
export function toTypesenseSearch(leg: MultiSearchLeg) {
  const filterBy = toTypesenseFilter(leg.filter);
  const query = leg.query.trim();
  const { queryBy, queryByWeights, numTypos, highlightAffixNumTokens } = leg.typesense;
  return {
    collection: leg.collection,
    q: query === '' ? '*' : query,
    query_by: queryBy,
    ...(queryByWeights === undefined ? {} : { query_by_weights: queryByWeights }),
    ...(numTypos === undefined ? {} : { num_typos: numTypos }),
    per_page: Math.min(leg.hitsPerPage, TYPESENSE_MAX_PER_PAGE),
    // Neither the stored vector nor contact PII ships to the browser.
    exclude_fields: BROWSER_EXCLUDE_FIELDS_PARAM,
    highlight_fields: queryBy,
    ...(highlightAffixNumTokens === undefined ? {} : { highlight_affix_num_tokens: highlightAffixNumTokens }),
    max_candidates: TYPESENSE_MAX_CANDIDATES,
    ...(leg.facets === undefined || leg.facets.length === 0 ? {} : { facet_by: leg.facets.join(',') }),
    ...(filterBy === '' ? {} : { filter_by: filterBy }),
  };
}

const MISSING_LEG: MultiSearchResult = { found: 0, hits: [], facetCounts: {}, error: 'Typesense returned no result' };

/**
 * Positional multi_search results, one per leg sent. A leg the engine rejected for its key
 * throws SearchAuthError: every leg of the request shares that key.
 */
export function parseTypesenseMultiSearch(response: unknown, legCount: number): MultiSearchResult[] {
  const { results } = multiSearchResponseSchema.parse(response);
  return Array.from({ length: legCount }, (_, index): MultiSearchResult => {
    const result = results[index];
    if (result === undefined) return MISSING_LEG;
    if (result.code !== undefined && isAuthStatus(result.code)) throw new SearchAuthError(result.code);
    return {
      found: result.found,
      hits: result.hits.map((hit) => ({
        document: hit.document,
        score: hit.text_match ?? 0,
        highlight: hit.highlight,
      })),
      facetCounts: Object.fromEntries(result.facet_counts.map((facet) => [facet.field_name, facet.counts])),
      ...(result.error === undefined ? {} : { error: result.error }),
    };
  });
}

export function createTypesenseTransport(endpoint: string, key: string): SearchTransport & MultiSearchTransport {
  return {
    async search<TDoc>({
      descriptor,
      query,
      filter,
      hitsPerPage,
      boosts,
      signal,
    }: Parameters<SearchTransport['search']>[0]) {
      const typesense = descriptor.ranking.typesense;
      const filterBy = toTypesenseFilter(filter);
      const trimmed = query.trim();
      const boostSort = toTypesenseBoostSort(boosts ?? []);
      // Boosts replace a descriptor's leading `_text_match:desc` with the bucketed form: Typesense allows three sort clauses.
      const descriptorSort = typesense?.sortBy ?? '';
      const sortBy = [
        boostSort === '' ? '' : `_text_match(buckets: 10):desc,${boostSort}`,
        boostSort === '' ? descriptorSort : descriptorSort.replace(/^_text_match:desc,?/, ''),
      ]
        .filter((clause) => clause !== '')
        .join(',');
      const mapDocument = DOCUMENT_MAPPERS[descriptor.entity];
      const body = JSON.stringify({
        searches: [
          {
            collection: descriptor.indexName.typesense,
            q: trimmed === '' ? '*' : trimmed,
            query_by: typesenseQueryBy(descriptor),
            per_page: Math.min(hitsPerPage, TYPESENSE_MAX_PER_PAGE),
            drop_tokens_threshold: 0,
            max_candidates: TYPESENSE_MAX_CANDIDATES,
            exclude_fields: BROWSER_EXCLUDE_FIELDS_PARAM,
            ...(filterBy === '' ? {} : { filter_by: filterBy }),
            ...(sortBy === '' ? {} : { sort_by: sortBy }),
          },
        ],
      });

      const leg = responseSchema.parse(await postMultiSearch(endpoint, key, body, signal)).results[0];
      if (!leg) throw new Error('Typesense returned no result');
      if (leg.error !== undefined) {
        if (leg.code !== undefined && isAuthStatus(leg.code)) throw new SearchAuthError(leg.code);
        throw new Error(`Typesense search failed: ${leg.error}`);
      }
      return (leg.hits ?? []).map((hit) => (mapDocument ? mapDocument(hit.document) : hit.document) as TDoc);
    },

    async multiSearch(legs, signal) {
      if (legs.length === 0) return [];
      const body = JSON.stringify({ searches: legs.map(toTypesenseSearch) });
      return parseTypesenseMultiSearch(await postMultiSearch(endpoint, key, body, signal), legs.length);
    },
  };
}
